import type { CallDirection, CallReason } from '../types'

/**
 * Semantic category for a disconnect reason. Two axes:
 *
 * - `success` — real conversation took place (duration > 10s, any reason).
 *   Resolved at display time by `getReasonDisplay`, not by reason alone.
 * - `callee_state` — about the called party (busy, rejected, ...). Yellow
 *   palette. Operator should usually retry later.
 * - `unanswered` — nobody picked up (no_answer, ringing_timeout). Orange.
 * - `technical` — infra/admin signal: phone health (no_sim, no_network),
 *   media (webrtc_*), relay/protocol (sync_timeout, agent_disconnected,
 *   browser_*, error). Red. Often calls for admin attention rather than
 *   operator retry.
 * - `unknown` — a reason token not in `reasonConfig` (backend↔web drift).
 *   Deliberately neutral: an unmapped token is NOT a confirmed technical
 *   failure. Only `unknownReasonDisplay` uses it.
 *
 * NB: this is the UI axis, NOT the counter axis. `consecutive_failed_calls`
 * increments on a SUBSET of phone-fault reasons (no_answer/busy/rejected/
 * no_sim/no_network/agent_lost_call/remote_hangup/restricted) — see backend
 * services/call.py _PHONE_FAULT_REASONS. The two axes cross: UI-category
 * `technical` includes no_sim/no_network (which DO increment) AND
 * webrtc_failed/sync_timeout/etc (which do NOT); UI-category `callee_state`
 * includes remote_hangup/restricted (which DO increment) AND
 * answered_elsewhere (which does NOT).
 */
export type ReasonCategory =
  | 'success'
  | 'callee_state'
  | 'unanswered'
  | 'technical'
  | 'unknown'

/**
 * Reasons the backend classifies into the `tech_failure` stats group
 * (services/stats_classification.py TECH_FAILURE). Mirrors that set
 * exactly — used by the stats Fleet Health panel to pick the
 * technical-reason rows out of a by_reason breakdown. NB: narrower than
 * the UI `technical` category, which also covers phone_revoked /
 * operator_revoked (admin-initiated, not a fault).
 */
export const TECH_FAILURE_REASONS: ReadonlySet<CallReason> = new Set([
  'webrtc_failed',
  'webrtc_setup_failed',
  'no_sim',
  'no_network',
  'agent_disconnected',
  'agent_lost_call',
  'sync_timeout',
  'state_timeout',
  'error',
  'browser_disconnected',
  'browser_replaced',
  'browser_reconnecting',
])

interface ReasonConfig {
  label: string
  color: string
  bg: string
  category: ReasonCategory
}

/**
 * Threshold that splits «real conversation» from «short blip». Mirrors
 * `ANSWERED_TALK_THRESHOLD_SECONDS` in `backend/app/services/stats_classification.py`
 * and `_talk_sum` in `repositories/stats.py`. A future bump touches both.
 */
export const ANSWERED_TALK_THRESHOLD_SECONDS = 10

// Incoming call reason='normal' with duration <= 10s means the operator
// hung up before a real conversation took place — agent acks the
// browser-side hangup with reason=normal, but talk-time threshold was
// never crossed. Showing "Answered" is misleading; render "Сброс
// менеджером" — explicit signal that operator dropped, not a technical
// failure.
export const rejectedDisplay: ReasonConfig = {
  label: 'Сброс менеджером',
  color: 'text-gray-500',
  bg: 'bg-gray-50 dark:bg-gray-800/30',
  category: 'callee_state',
}

// Outgoing call reason='normal' with duration <= 10s means the operator
// cancelled the dial before the callee picked up, OR there was a short
// blip («алло — *клик*»). Either way not a real conversation.
export const cancelledDisplay: ReasonConfig = {
  label: 'Сброс менеджером',
  color: 'text-gray-500',
  bg: 'bg-gray-50 dark:bg-gray-800/30',
  category: 'unanswered',
}

// Incoming no_answer / ringing_timeout — somebody dialed the SIM and the
// call was never picked up. Distinct label from an outgoing недозвон
// («Не отвечает»): a missed inbound call is not an operator dial failure,
// it is just a call the operator did not take.
export const missedDisplay: ReasonConfig = {
  label: 'Пропущенный',
  color: 'text-orange-600',
  bg: 'bg-orange-50 dark:bg-orange-950/30',
  category: 'unanswered',
}

// Fallback for a reason token outside `reasonConfig` — only reachable on
// backend↔web drift (a `CallEndedEvent.reason` Literal value the `CallReason`
// union hasn't mirrored yet; see types/index.ts contract note). Render it
// neutral gray with the RAW token, NOT red «Ошибка»: an unmapped token is an
// unknown disconnect, not a confirmed technical failure, and the raw token is
// the most useful thing to surface for diagnosis. `category` is inert today
// (nothing reads ReasonConfig.category) — but it's set to the neutral
// `unknown`, NOT `technical`, so if anything ever does read it an unmapped
// token isn't misclassified as a confirmed technical failure.
function unknownReasonDisplay(reason: string): ReasonConfig {
  return {
    label: reason || 'Неизвестно',
    color: 'text-gray-500',
    bg: 'bg-gray-50 dark:bg-gray-800/30',
    category: 'unknown',
  }
}

/**
 * Resolve display config for a finished call. Duration-first invariant:
 *
 * - `duration > 10` ⟹ "Отвечен" (real conversation) regardless of reason.
 *   A 23-минутный `remote_hangup` is a successful call that ended when
 *   the callee hung up, not a `Удалённый сброс`.
 * - `duration <= 10` + `reason='normal'` ⟹ operator-side drop
 *   (rejected on incoming, cancelled on outgoing) — same gray label.
 * - `duration <= 10` + unanswered reason (`no_answer`/`ringing_timeout`)
 *   ⟹ "Пропущенный" on incoming, "Не отвечает" on outgoing — a missed
 *   inbound call vs an operator недозвон.
 * - everything else falls through to the reason-specific config.
 *
 * Use when you HAVE direction + duration (CallHistoryPage). For phone-level
 * `lastDisconnectReason` (no direction/duration) use `getReasonLabel` below.
 */
export function getReasonDisplay(
  reason: CallReason,
  direction: CallDirection,
  durationSeconds: number,
): ReasonConfig {
  if (durationSeconds > ANSWERED_TALK_THRESHOLD_SECONDS) {
    return reasonConfig.normal
  }
  if (reason === 'normal') {
    return direction === 'incoming' ? rejectedDisplay : cancelledDisplay
  }
  // Same reason codes, different meaning by direction: an incoming
  // unanswered call is "Пропущенный", an outgoing one stays "Не отвечает".
  if (reason === 'no_answer' || reason === 'ringing_timeout') {
    return direction === 'incoming' ? missedDisplay : reasonConfig[reason]
  }
  return reasonConfig[reason] ?? unknownReasonDisplay(reason)
}

/**
 * Phone-level lookup for `lastDisconnectReason` badge — no direction or
 * duration available, so the `getReasonDisplay` duration override is not
 * applicable here. Operators who need the "Cancelled vs Answered"
 * distinction see it in CallHistoryPage where the full row carries
 * direction + duration_seconds.
 */
export function getReasonLabel(reason: CallReason): ReasonConfig {
  return reasonConfig[reason] ?? unknownReasonDisplay(reason)
}

export const reasonConfig: Record<CallReason, ReasonConfig> = {
  // Success
  normal: { label: 'Отвечен', color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-950/30', category: 'success' },
  // Callee state (about the called party — yellow palette)
  busy: { label: 'Занято', color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-950/30', category: 'callee_state' },
  user_busy: { label: 'Занято', color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-950/30', category: 'callee_state' },
  phone_busy: { label: 'Занято', color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-950/30', category: 'callee_state' },
  rejected: { label: 'Сброс', color: 'text-yellow-700', bg: 'bg-yellow-50 dark:bg-yellow-950/30', category: 'callee_state' },
  restricted: { label: 'Ограничение оператора', color: 'text-yellow-700', bg: 'bg-yellow-50 dark:bg-yellow-950/30', category: 'callee_state' },
  answered_elsewhere: { label: 'Отвечен на другом', color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-800/30', category: 'callee_state' },
  remote_hangup: { label: 'Удалённый сброс', color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-800/30', category: 'callee_state' },
  // Unanswered (orange)
  no_answer: { label: 'Не отвечает', color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-950/30', category: 'unanswered' },
  ringing_timeout: { label: 'Не отвечает', color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-950/30', category: 'unanswered' },
  // Technical (red — admin signal)
  no_sim: { label: 'Нет SIM', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  no_network: { label: 'Нет сети', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  agent_lost_call: { label: 'Сбой связи', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  sync_timeout: { label: 'Сбой связи', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  state_timeout: { label: 'Сбой связи', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  webrtc_failed: { label: 'Сбой связи', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  webrtc_setup_failed: { label: 'Сбой связи', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  agent_disconnected: { label: 'Сбой связи', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  browser_disconnected: { label: 'Сбой связи', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  browser_reconnecting: { label: 'Сбой связи', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
  browser_replaced: { label: 'Заменено', color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-800/30', category: 'technical' },
  // Admin-initiated revoke — neutral gray, NOT red. The call was healthy
  // when admin pulled access; rendering it as "Error" misleads operators
  // into thinking the system failed.
  phone_revoked: { label: 'Телефон отозван', color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-800/30', category: 'technical' },
  operator_revoked: { label: 'Доступ отозван', color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-800/30', category: 'technical' },
  error: { label: 'Ошибка', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', category: 'technical' },
}
